/**
 * AddOnSelector - Add-ons grid for extra items
 * Supports quantity with +/- buttons and syrup exclusivity
 */
import { memo } from 'react';
import { Plus, Minus, X } from 'lucide-react';

// Syrup names for exclusive selection
const SYRUP_NAMES = ['caramel', 'hazelnut', 'vanilla', 'caramel syrup', 'hazelnut syrup', 'vanilla syrup'];

const isSyrup = (addonName) => {
  const nameLower = addonName?.toLowerCase() || '';
  return SYRUP_NAMES.some(syrup => nameLower.includes(syrup));
};

const AddOnSelector = memo(function AddOnSelector({ 
  addOns = [], 
  selectedAddOns = [], 
  onToggleAddOn,
  onIncreaseQuantity,
  onDecreaseQuantity
}) {
  if (addOns.length === 0) return null;

  // Check if a syrup is already selected
  const selectedSyrup = selectedAddOns.find(a => isSyrup(a.name));

  return (
    <div>
      <h4 className="font-medium text-base mb-2">Add Extra</h4>
      <div className="grid grid-cols-2 gap-2">
        {addOns.map(addon => {
          const selected = selectedAddOns.find(a => a.name === addon.name);
          const isSelected = !!selected;
          const quantity = selected?.quantity || 0;
          const isAddonSyrup = isSyrup(addon.name);
          
          // Disable other syrups if one is already selected
          const isDisabled = isAddonSyrup && selectedSyrup && selectedSyrup.name !== addon.name;
          
          return (
            <div
              key={addon.id || addon.name}
              data-testid={`addon-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`p-3 rounded-xl transition-all border-2 ${
                isSelected
                  ? 'border-primary bg-primary/10'
                  : isDisabled
                    ? 'border-border bg-muted/50 opacity-50 cursor-not-allowed'
                    : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              {!isSelected ? (
                // Not selected - show add button
                <button
                  onClick={() => !isDisabled && onToggleAddOn(addon)}
                  disabled={isDisabled}
                  className="w-full flex justify-between items-center"
                >
                  <span className={`font-medium text-sm ${isDisabled ? 'text-muted-foreground' : ''}`}>
                    {addon.name}
                    {isAddonSyrup && <span className="text-xs text-muted-foreground ml-1">(syrup)</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-primary text-sm font-medium">+${addon.price.toFixed(2)}</span>
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <Plus className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                </button>
              ) : (
                // Selected - show quantity controls
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <span className="font-medium text-sm block">{addon.name}</span>
                    <span className="text-primary text-xs font-medium">
                      ${(addon.price * quantity).toFixed(2)} ({quantity}x ${addon.price.toFixed(2)})
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Decrease / Remove button */}
                    <button
                      onClick={() => onDecreaseQuantity ? onDecreaseQuantity(addon.name) : onToggleAddOn(addon)}
                      className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                      data-testid={`addon-decrease-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {quantity === 1 ? (
                        <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <Minus className="w-4 h-4 text-red-600 dark:text-red-400" />
                      )}
                    </button>
                    
                    {/* Quantity display */}
                    <span className="w-8 text-center font-bold text-lg">{quantity}</span>
                    
                    {/* Increase button */}
                    <button
                      onClick={() => onIncreaseQuantity ? onIncreaseQuantity(addon.name) : null}
                      className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                      data-testid={`addon-increase-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Plus className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Syrup exclusivity note */}
      {selectedSyrup && (
        <p className="text-xs text-muted-foreground mt-2 italic">
          Only one syrup flavor can be selected. You can add multiple shots of {selectedSyrup.name}.
        </p>
      )}
    </div>
  );
});

export default AddOnSelector;
